'use client';
import { useMemo } from 'react';
import { BarChart3 } from 'lucide-react';
import type { Lead } from './types';

const DAY_MS = 86_400_000;
const GOLD = '#E8FF00';
const GREEN = '#22C55E';
const BLUE = '#3B82F6';
const PURPLE = '#A855F7';
const GRAY = '#737373';

interface DailyPoint {
  label: string;
  submissions: number;
  verifications: number;
}

interface DonutSegment {
  label: string;
  value: number;
  color: string;
}

interface RecentRow extends DailyPoint {
  cumulative: number;
}

function KpiCard({ label, value, sub, valueClass = 'text-white' }: {
  label: string;
  value: string;
  sub?: string;
  valueClass?: string;
}) {
  return (
    <div className="card-dark">
      <p className="text-[11px] font-mono uppercase tracking-wider text-gray-500">{label}</p>
      <p className={`text-3xl font-black mt-2 ${valueClass}`}>{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
    </div>
  );
}

function TrendChart({ series }: { series: DailyPoint[] }) {
  const W = 640;
  const H = 240;
  const padL = 36;
  const padR = 12;
  const padT = 14;
  const padB = 28;
  const iw = W - padL - padR;
  const ih = H - padT - padB;
  const max = Math.max(1, ...series.map(s => Math.max(s.submissions, s.verifications)));
  const x = (i: number) => padL + (series.length <= 1 ? iw / 2 : (i / (series.length - 1)) * iw);
  const y = (v: number) => padT + ih - (v / max) * ih;
  const line = (key: 'submissions' | 'verifications') =>
    series.map((s, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(s[key]).toFixed(1)}`).join(' ');
  const area = `${line('submissions')} L${x(series.length - 1).toFixed(1)},${(padT + ih).toFixed(1)} L${x(0).toFixed(1)},${(padT + ih).toFixed(1)} Z`;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" role="img" aria-label="近 14 日 Leads 趨勢">
      <defs>
        <linearGradient id="goldArea" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={GOLD} stopOpacity="0.35" />
          <stop offset="100%" stopColor={GOLD} stopOpacity="0.02" />
        </linearGradient>
      </defs>
      {[0, 0.5, 1].map(f => (
        <g key={f}>
          <line x1={padL} x2={W - padR} y1={y(max * f)} y2={y(max * f)} stroke="#2D2D2D" strokeWidth="1" />
          <text x={padL - 6} y={y(max * f) + 3.5} textAnchor="end" fill={GRAY} fontSize="10">
            {Math.round(max * f)}
          </text>
        </g>
      ))}
      <path d={area} fill="url(#goldArea)" />
      <path d={line('submissions')} fill="none" stroke={GOLD} strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
      <path d={line('verifications')} fill="none" stroke={GREEN} strokeWidth="2" strokeDasharray="5 4" strokeLinejoin="round" strokeLinecap="round" />
      {series.map((s, i) => (
        <circle key={s.label} cx={x(i)} cy={y(s.submissions)} r="3" fill={GOLD} />
      ))}
      {series.map((s, i) => (i % 2 === 0) && (
        <text key={`x-${s.label}`} x={x(i)} y={H - 8} textAnchor="middle" fill={GRAY} fontSize="10">{s.label}</text>
      ))}
    </svg>
  );
}

function DonutChart({ segments, centerValue, centerLabel }: {
  segments: DonutSegment[];
  centerValue: string;
  centerLabel: string;
}) {
  const total = segments.reduce((sum, s) => sum + s.value, 0);
  const C = 2 * Math.PI * 52;
  let acc = 0;

  return (
    <div className="flex items-center gap-6 flex-wrap">
      <svg viewBox="0 0 140 140" className="w-36 h-36 shrink-0" role="img">
        <g transform="rotate(-90 70 70)">
          <circle cx="70" cy="70" r="52" fill="none" stroke="#2D2D2D" strokeWidth="14" />
          {total > 0 && segments.map(seg => {
            const frac = seg.value / total;
            const el = (
              <circle
                key={seg.label}
                cx="70"
                cy="70"
                r="52"
                fill="none"
                stroke={seg.color}
                strokeWidth="14"
                strokeDasharray={`${(frac * C).toFixed(2)} ${(C - frac * C).toFixed(2)}`}
                strokeDashoffset={(-acc * C).toFixed(2)}
              />
            );
            acc += frac;
            return el;
          })}
        </g>
        <text x="70" y="66" textAnchor="middle" fill="#FFFFFF" fontSize="24" fontWeight="900">{centerValue}</text>
        <text x="70" y="86" textAnchor="middle" fill={GRAY} fontSize="10">{centerLabel}</text>
      </svg>
      <ul className="space-y-2 min-w-[150px]">
        {segments.map(seg => (
          <li key={seg.label} className="flex items-center gap-2 text-sm">
            <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: seg.color }} />
            <span className="text-gray-300">{seg.label}</span>
            <span className="ml-auto font-mono text-gray-400">
              {seg.value}
              <span className="text-gray-500">（{total > 0 ? Math.round((seg.value / total) * 100) : 0}%）</span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function HourlyChart({ hours }: { hours: number[] }) {
  const W = 320;
  const H = 150;
  const padT = 10;
  const padB = 20;
  const ih = H - padT - padB;
  const max = Math.max(1, ...hours);
  const bw = (W / 24) * 0.65;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" role="img" aria-label="提交時段分佈">
      {hours.map((v, h) => {
        const bh = (v / max) * ih;
        const cx = (h + 0.5) * (W / 24);
        return (
          <rect
            key={h}
            x={cx - bw / 2}
            y={padT + ih - bh}
            width={bw}
            height={Math.max(v > 0 ? 2 : 0, bh)}
            rx="1.5"
            fill={v > 0 ? GOLD : '#2D2D2D'}
          />
        );
      })}
      {[0, 6, 12, 18].map(h => (
        <text key={h} x={(h + 0.5) * (W / 24)} y={H - 5} textAnchor="middle" fill={GRAY} fontSize="9">{h}時</text>
      ))}
    </svg>
  );
}

export default function AnalyticsTab({ leads, loading }: { leads: Lead[]; loading: boolean }) {
  const stats = useMemo(() => {
    const total = leads.length;
    const verified = leads.filter(l => l.verified).length;
    const pending = total - verified;
    const verifiedPct = total > 0 ? Math.round((verified / total) * 100) : 0;
    const contact = leads.filter(l => l.type === 'contact').length;
    const newsletter = total - contact;

    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    const todayStart = startOfToday.getTime();
    const now = Date.now();
    const today = leads.filter(l => l.timestamp >= todayStart).length;
    const last7 = leads.filter(l => l.timestamp >= now - 7 * DAY_MS).length;
    const prev7 = leads.filter(l => l.timestamp >= now - 14 * DAY_MS && l.timestamp < now - 7 * DAY_MS).length;

    const gaps = leads
      .filter(l => l.verified && l.verifiedAt)
      .map(l => (l.verifiedAt! - l.timestamp) / 3_600_000);
    const avgGapH = gaps.length > 0 ? gaps.reduce((a, b) => a + b, 0) / gaps.length : null;

    const dayKey = (ts: number) => {
      const d = new Date(ts);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    };
    const submissionsByDay = new Map<string, number>();
    const verificationsByDay = new Map<string, number>();
    leads.forEach(l => {
      const submitKey = dayKey(l.timestamp);
      submissionsByDay.set(submitKey, (submissionsByDay.get(submitKey) ?? 0) + 1);
      if (l.verified && l.verifiedAt) {
        const verifyKey = dayKey(l.verifiedAt);
        verificationsByDay.set(verifyKey, (verificationsByDay.get(verifyKey) ?? 0) + 1);
      }
    });
    const series: DailyPoint[] = Array.from({ length: 14 }, (_, i) => {
      const d = new Date(todayStart - (13 - i) * DAY_MS);
      const key = dayKey(d.getTime());
      return {
        label: `${d.getMonth() + 1}/${d.getDate()}`,
        submissions: submissionsByDay.get(key) ?? 0,
        verifications: verificationsByDay.get(key) ?? 0,
      };
    });

    const hours = Array.from({ length: 24 }, (_, h) => leads.filter(l => new Date(l.timestamp).getHours() === h).length);

    const windowSubs = series.reduce((a, s) => a + s.submissions, 0);
    let cum = total - windowSubs;
    const cumulative: number[] = [];
    series.forEach(s => {
      cum += s.submissions;
      cumulative.push(cum);
    });
    const recentRows: RecentRow[] = series
      .slice(-7)
      .map((s, i) => ({ ...s, cumulative: cumulative[series.length - 7 + i] }))
      .reverse();

    return { total, verified, pending, verifiedPct, contact, newsletter, today, last7, prev7, avgGapH, series, hours, recentRows };
  }, [leads]);

  if (loading) {
    return <div className="text-center py-20 text-gray-500">載入中…</div>;
  }

  if (stats.total === 0) {
    return (
      <div className="card-dark text-center py-16">
        <BarChart3 className="w-10 h-10 text-jkd-gold/50 mx-auto mb-4" />
        <p className="text-gray-400">暫無數據 — 等第一筆 Leads 進來，這裡就會出現分析圖表</p>
      </div>
    );
  }

  const deltaText = `較前 7 日 ${stats.prev7 <= stats.last7 ? '+' : ''}${stats.last7 - stats.prev7}`;
  const avgText = stats.avgGapH === null
    ? '—'
    : stats.avgGapH < 1
      ? `${Math.round(stats.avgGapH * 60)} 分鐘`
      : `${stats.avgGapH.toFixed(1)} 小時`;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
        <KpiCard label="總 Leads" value={String(stats.total)} sub={`聯絡查詢 ${stats.contact} ・ 心法通訊 ${stats.newsletter}`} />
        <KpiCard label="已驗證" value={String(stats.verified)} sub={`驗證率 ${stats.verifiedPct}%`} valueClass="text-green-400" />
        <KpiCard label="待跟進" value={String(stats.pending)} sub="未完成 WhatsApp 驗證" valueClass="text-yellow-400" />
        <KpiCard label="近 7 日新增" value={String(stats.last7)} sub={deltaText} />
        <KpiCard label="今日新增" value={String(stats.today)} />
        <KpiCard label="平均驗證時間" value={avgText} sub="提交 → WhatsApp 驗證" valueClass="text-jkd-gold" />
      </div>

      <div className="card-dark">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
          <h2 className="text-lg font-bold text-white">近 14 日趨勢</h2>
          <div className="flex items-center gap-4 text-xs text-gray-400">
            <span className="inline-flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: GOLD }} />提交
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="w-2.5 h-0.5" style={{ backgroundColor: GREEN }} />已驗證
            </span>
          </div>
        </div>
        <TrendChart series={stats.series} />
      </div>

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        <div className="card-dark">
          <h2 className="text-lg font-bold text-white mb-4">類型分佈</h2>
          <DonutChart
            segments={[
              { label: '聯絡查詢', value: stats.contact, color: BLUE },
              { label: '心法通訊', value: stats.newsletter, color: PURPLE },
            ]}
            centerValue={String(stats.total)}
            centerLabel="總數"
          />
        </div>
        <div className="card-dark">
          <h2 className="text-lg font-bold text-white mb-4">驗證狀態</h2>
          <DonutChart
            segments={[
              { label: '已驗證', value: stats.verified, color: GREEN },
              { label: '待驗證', value: stats.pending, color: GRAY },
            ]}
            centerValue={`${stats.verifiedPct}%`}
            centerLabel="驗證率"
          />
        </div>
        <div className="card-dark">
          <h2 className="text-lg font-bold text-white mb-4">提交時段分佈</h2>
          <HourlyChart hours={stats.hours} />
        </div>
      </div>

      <div className="card-dark overflow-x-auto">
        <h2 className="text-lg font-bold text-white mb-4">近 7 日摘要</h2>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-gray-500 text-xs font-mono uppercase tracking-wider border-b border-jkd-gray-200">
              <th className="py-2 pr-4">日期</th>
              <th className="py-2 pr-4">新增</th>
              <th className="py-2 pr-4">已驗證</th>
              <th className="py-2">累計</th>
            </tr>
          </thead>
          <tbody>
            {stats.recentRows.map(r => (
              <tr key={r.label} className="border-b border-jkd-gray-200/50 last:border-0">
                <td className="py-2 pr-4 text-gray-300 font-mono">{r.label}</td>
                <td className="py-2 pr-4 text-white font-bold">{r.submissions}</td>
                <td className="py-2 pr-4 text-green-400">{r.verifications}</td>
                <td className="py-2 text-gray-400 font-mono">{r.cumulative}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
